import { RefObject } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface CameraPreviewProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  isInitializing: boolean;
  error: string | null;
  facingMode?: 'user' | 'environment';
  mirror?: boolean;
  initializingLabel?: string;
}

export const CameraPreview = ({
  videoRef,
  canvasRef,
  isInitializing,
  error,
  facingMode,
  mirror = false,
  initializingLabel = 'Starting camera...',
}: CameraPreviewProps) => {
  const shouldMirror = mirror || facingMode === 'user';
  return (
    <>
      {isInitializing && (
        <Box sx={{ color: 'common.white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography color="inherit">{initializingLabel}</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error.light">{error}</Typography>
        </Box>
      )}

      <Box
        component="video"
        ref={videoRef}
        autoPlay
        playsInline
        muted
        sx={{
          width: '100%',
          height: '100%',
          // 'contain' so the on-screen frame matches what capturePhoto draws (full
          // videoWidth/videoHeight). 'cover' would crop the preview while the saved
          // image kept the cropped-out edges, surprising the user.
          objectFit: 'contain',
          display: isInitializing || error ? 'none' : 'block',
          transform: shouldMirror ? 'scaleX(-1)' : 'none',
        }}
      />

      <Box component="canvas" ref={canvasRef} sx={{ display: 'none' }} />
    </>
  );
};
